import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.phone || form.email) {
      try {
        const { data } = await api.get("/leads/duplicate-check", { params: { phone: form.phone || "", email: form.email || "" } });
        if (data.matches.length > 0) {
          const names = data.matches.map((m) => `${m.type}: ${m.name} (${m.code})`).join("\n");
          if (!window.confirm(`Possible duplicate found:\n${names}\n\nCreate a new customer anyway?`)) return;
        }
      } catch { /* non-blocking */ }
    }
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null };
      const { data } = await api.post("/customers", payload);
      toast.success(`Created ${data.customer_code}`); setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <AppShell title="Customers">
      <div className="flex justify-end mb-4">
        {can("customers.create") && (
          <button onClick={() => setOpen(true)} data-testid="customers-new-button" className="flex items-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium px-4 py-2 transition-colors shadow-sm"><Plus size={16} /> New Customer</button>
        )}
      </div>
      <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]">
            <th className="px-5 py-3">Customer ID</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Status</th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`customers-row-${c.customer_code}`} className="border-t border-[#EEE9DE] hover:bg-[#EEE9DE] transition-colors">
                <td className="px-5 py-3.5 mono text-neutral-700">{c.customer_code}</td>
                <td className="px-5 py-3.5 text-neutral-900 font-medium">{c.first_name} {c.last_name}</td>
                <td className="px-5 py-3.5 text-neutral-600 mono">{c.phone || "—"}</td>
                <td className="px-5 py-3.5 text-neutral-600">{c.email || "—"}</td>
                <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-neutral-400">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">New Customer</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">First name</label><input data-testid="new-customer-first-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" /></div>
              <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Last name</label><input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" /></div>
            </div>
            <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Phone</label><input data-testid="new-customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" placeholder="+91…" /></div>
            <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Email</label><input data-testid="new-customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" /></div>
            <button data-testid="new-customer-submit" disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">{saving && <Loader2 size={16} className="animate-spin" />} Create Customer</button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
